import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import localeText from "../../../../locale";
import ProductService from "../../../../service/ProductService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";

const productService = new ProductService();

const EMPTY_FORM = {
  physicalStoreName: "",
  physicalAddress: "",
  physicalNumber: "",
  physicalDistrict: "",
  physicalCity: "",
  physicalState: "",
  physicalPostalCode: "",
  physicalCountry: "",
};

const SubmitProofDialog = ({ visible, onHide, onSubmit, loading, isPending, rejectionReason }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [photoName, setPhotoName] = useState("");
  const [proofPhotoId, setProofPhotoId] = useState(null);
  const [proofPhotoUrl, setProofPhotoUrl] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const { showErrorToast } = useToastContext();

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    if (!visible) {
      setForm(EMPTY_FORM);
      setPhotoName("");
      setProofPhotoId(null);
      setProofPhotoUrl(null);
      setIsUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [visible]);

  const extractCloudinaryId = (url) => url.split("/").pop().split(".")[0];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoName(file.name);
    setProofPhotoId(null);
    setIsUploadingPhoto(true);

    try {
      const res = await productService.postUploadFile(null, file);

      if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        setPhotoName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setProofPhotoUrl(res.result);
      setProofPhotoId(extractCloudinaryId(res.result));
    } catch {
      showErrorToast({ code: 500000 });
      setPhotoName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleClearPhoto = (e) => {
    e.stopPropagation();
    setPhotoName("");
    setProofPhotoId(null);
    setProofPhotoUrl(null);
    setIsUploadingPhoto(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const canSubmit =
    form.physicalStoreName.trim() &&
    form.physicalAddress.trim() &&
    form.physicalNumber.trim() &&
    form.physicalCity.trim() &&
    form.physicalCountry.trim() &&
    proofPhotoId &&
    !isUploadingPhoto;

  const handleSubmit = () => {
    onSubmit({ ...form, proofPhotoId, proofPhotoUrl });
  };

  return (
    <Dialog
      header={
        <div className="flex flex-column gap-1">
          <div className="text-lg font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("physicalStoreVerificationTitle")}
          </div>
          <div className="text-sm font-normal" style={{ color: "var(--theme-text-muted)" }}>
            {isPending
              ? localeText("physicalStoreVerificationPendingSubtitle")
              : localeText("physicalStoreVerificationSubtitle")}
          </div>
        </div>
      }
      visible={visible}
      className="w-30rem m-2"
      onHide={onHide}
      draggable={false}
    >
      {isPending ? (
        <div className="flex flex-column align-items-center gap-3 py-4">
          <div
            className="border-circle flex align-items-center justify-content-center"
            style={{
              width: "64px",
              height: "64px",
              backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
            }}
          >
            <i className="pi pi-clock text-2xl" style={{ color: "var(--theme-text-muted)" }} />
          </div>
          <p className="m-0 text-sm text-center" style={{ color: "var(--theme-text-muted)" }}>
            {localeText("physicalStoreVerificationPendingMessage")}
          </p>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          {rejectionReason && (
            <div
              className="flex align-items-start gap-2 border-round-md p-3"
              style={{
                backgroundColor: "color-mix(in srgb, #eb4034 12%, transparent)",
                border: "1px solid color-mix(in srgb, #eb4034 30%, transparent)",
              }}
            >
              <i
                className="pi pi-times-circle text-sm mt-1"
                style={{ color: "#eb4034", flexShrink: 0 }}
              />
              <div className="flex flex-column gap-1">
                <span className="font-semibold text-sm" style={{ color: "#eb4034" }}>
                  {localeText("physicalStoreVerificationRejected")}
                </span>
                <span className="text-sm" style={{ color: "var(--theme-text)" }}>
                  {rejectionReason}
                </span>
              </div>
            </div>
          )}

          <Field label={localeText("physicalStoreName")}>
            <InputText
              value={form.physicalStoreName}
              onChange={setField("physicalStoreName")}
              placeholder={localeText("physicalStoreName")}
            />
          </Field>

          <div className="grid m-0 gap-0">
            <div className="col-8 pl-0 pr-1">
              <Field label={localeText("address")}>
                <InputText
                  value={form.physicalAddress}
                  onChange={setField("physicalAddress")}
                  placeholder={localeText("physicalStoreVerificationAddressPlaceholder")}
                />
              </Field>
            </div>
            <div className="col-4 pr-0 pl-1">
              <Field label={localeText("number")}>
                <InputText
                  value={form.physicalNumber}
                  onChange={setField("physicalNumber")}
                  placeholder={localeText("physicalStoreVerificationNumberPlaceholder")}
                />
              </Field>
            </div>
          </div>

          <div className="grid m-0 gap-0">
            <div className="col-7 pl-0 pr-1">
              <Field label={localeText("district")}>
                <InputText
                  value={form.physicalDistrict}
                  onChange={setField("physicalDistrict")}
                  placeholder={localeText("district")}
                />
              </Field>
            </div>
            <div className="col-5 pr-0 pl-1">
              <Field label={localeText("physicalStoreConventionPostalCode")}>
                <InputText
                  value={form.physicalPostalCode}
                  onChange={setField("physicalPostalCode")}
                  placeholder={localeText("physicalStoreVerificationPostalCodePlaceholder")}
                />
              </Field>
            </div>
          </div>

          <div className="grid m-0 gap-0">
            <div className="col-7 pl-0 pr-1">
              <Field label={localeText("city")}>
                <InputText
                  value={form.physicalCity}
                  onChange={setField("physicalCity")}
                  placeholder={localeText("city")}
                />
              </Field>
            </div>
            <div className="col-5 pr-0 pl-1">
              <Field label={localeText("state")}>
                <InputText
                  value={form.physicalState}
                  onChange={setField("physicalState")}
                  placeholder={localeText("physicalStoreVerificationStatePlaceholder")}
                />
              </Field>
            </div>
          </div>

          <Field label={localeText("country")}>
            <InputText
              value={form.physicalCountry}
              onChange={setField("physicalCountry")}
              placeholder={localeText("country")}
            />
          </Field>

          <Field label={localeText("physicalStoreConventionProofPhoto")}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div
              className="flex align-items-center gap-2 border-round-md p-2 cursor-pointer"
              style={{ border: "1px solid var(--theme-border, #EBEBEB)", minHeight: "42px" }}
              onClick={() => !isUploadingPhoto && fileInputRef.current?.click()}
            >
              {isUploadingPhoto ? (
                <i
                  className="pi pi-spin pi-spinner text-sm"
                  style={{ color: "var(--theme-text-muted)" }}
                />
              ) : (
                <i
                  className={`pi ${proofPhotoId ? "pi-check-circle" : "pi-image"} text-sm`}
                  style={{
                    color: proofPhotoId
                      ? "var(--theme-primary, #1DAF61)"
                      : "var(--theme-text-muted)",
                  }}
                />
              )}
              <span
                className="text-sm flex-1"
                style={{
                  color: photoName ? "var(--theme-text)" : "var(--theme-text-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isUploadingPhoto
                  ? localeText("physicalStoreConventionUploading")
                  : photoName || localeText("physicalStoreConventionProofPhotoPlaceholder")}
              </span>
              {photoName && !isUploadingPhoto && (
                <i
                  className="pi pi-times text-xs cursor-pointer"
                  style={{ color: "var(--theme-text-muted)" }}
                  onClick={handleClearPhoto}
                />
              )}
            </div>
          </Field>

          <div className="flex justify-content-end mt-1">
            <Button
              label={localeText("send")}
              icon="pi pi-send"
              loading={loading}
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="border-round-md border-none p-no-outline"
              style={{ backgroundColor: "var(--theme-primary, #1DAF61)", color: "#fff" }}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-column gap-1">
    <label className="font-semibold text-sm" style={{ color: "var(--theme-text-muted)" }}>
      {label}
    </label>
    {children}
  </div>
);

export default SubmitProofDialog;
